// src/pages/CreatorProfile.js
// A public creator page: avatar, stats, follow/unfollow, and their Drift posts.
//   GET /social/creator/{id} · /social/creator/{id}/drifts · POST/DELETE /social/follow/{id}

import { useCallback, useEffect, useState } from "react";
import Avatar from "../components/Avatar";
import MediaCard from "../components/MediaCard";
import PostDetail from "../components/PostDetail";
import { social, drift } from "../api";

const fmt = (n = 0) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

export default function CreatorProfile({ userId, user, onRequireAuth, onRemix, onBack }) {
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [err, setErr] = useState("");
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [p, d] = await Promise.all([
        social.creator(userId),
        social.creatorDrifts(userId).catch(() => ({ posts: [] })),
      ]);
      setProfile(p);
      setFollowing(!!p.is_following);
      setFollowers(p.followers || 0);
      setPosts(d.posts || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const isSelf = user && profile && user.id === profile.user_id;

  const toggleFollow = async () => {
    if (!user) return onRequireAuth();
    // optimistic
    const next = !following;
    setFollowing(next);
    setFollowers((c) => c + (next ? 1 : -1));
    try {
      next ? await social.follow(userId) : await social.unfollow(userId);
    } catch (e) {
      setFollowing(!next);
      setFollowers((c) => c + (next ? -1 : 1));
      setErr(e.message);
    }
  };

  const like = async (post) => {
    if (!user) return onRequireAuth();
    try {
      const r = await drift.like(post._id);
      setPosts((ps) => ps.map((x) => (x._id === post._id ? { ...x, likes_count: r.likes } : x)));
    } catch (_) {}
  };

  if (loading) return <div className="page"><div className="empty">Loading…</div></div>;
  if (err) return <div className="page"><div className="form-error center">{err}</div></div>;
  if (!profile) return null;

  const STATS = [
    ["Drifts", profile.drifts],
    ["Followers", followers],
    ["Likes", profile.likes],
    ["Remixes", profile.remixes],
  ];

  return (
    <div className="page">
      <button className="iconbtn" onClick={onBack} aria-label="Back" style={{ marginBottom: 16 }}>←</button>

      <div className="creator">
        <Avatar name={profile.username} url={profile.avatar_url || profile.profile_picture} size={84} />
        <div className="creator__info">
          <div className="creator__name">@{profile.username}</div>
          <div className="creator__stats">
            {STATS.map(([label, val]) => (
              <div key={label} className="creator__stat">
                <b>{fmt(val || 0)}</b>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
        {!isSelf && (
          <button className={`btn ${following ? "btn--ghost" : "btn--primary"}`} onClick={toggleFollow}>
            {following ? "Following" : "Follow"}
          </button>
        )}
      </div>

      <h3 className="history__title" style={{ marginTop: 26 }}>Drifts</h3>
      {posts.length === 0 ? (
        <div className="empty">No public drifts yet.</div>
      ) : (
        <div className="masonry">
          {posts.map((p) => (
            <MediaCard key={p._id} post={p} onOpen={setViewing} onLike={like} onRemix={onRemix} />
          ))}
        </div>
      )}

      {viewing && (
        <PostDetail
          post={viewing}
          user={user}
          onClose={() => setViewing(null)}
          onRequireAuth={onRequireAuth}
          onRemix={onRemix}
          onLike={like}
        />
      )}
    </div>
  );
}
